import { useAuthMutations, useUserByIdQuery } from '@scoring/api';
import { useAuthenticationStore } from '@scoring/state';
import { type Credentials, type EventDetailedModel, type SeriesModel, UserRole } from '@scoring/types';
import React from 'react';
import { DateTime } from 'ts-luxon';

export const hasTokenExpired = (expiresAt?: string) => {
    const expiry = expiresAt ? DateTime.fromISO(expiresAt) : undefined;
    return !expiry?.isValid || expiry <= DateTime.now();
};

export const useAuthentication = () => {
    const commands = useAuthMutations();
    const authorization = useAuthenticationStore((state) => state.authorization);
    const credentials = useAuthenticationStore((state) => state.credentials);
    const user = useAuthenticationStore((state) => state.user);
    const hydrated = useAuthenticationStore((state) => state.hydrated);
    const setUser = useAuthenticationStore((state) => state.setUser);
    const loginStore = useAuthenticationStore((state) => state.login);
    const authorizeStore = useAuthenticationStore((state) => state.authorize);
    const logoutStore = useAuthenticationStore((state) => state.logout);
    const { data: queryUser, setUserId, refetch: refetchUser } = useUserByIdQuery();

    //Keep userId in sync with stored authorization (handles hydration and re-mounts)
    //An expired token would only get a 401, so wait for useAuthTokenUpdater to replace it
    React.useEffect(() => {
        setUserId(hasTokenExpired(authorization?.expiresAt) ? undefined : authorization?.id);
    }, [authorization?.id, authorization?.expiresAt, setUserId]);

    //Wait for query to return user data and set it in the store
    React.useEffect(() => {
        if (queryUser && queryUser !== user) {
            setUser(queryUser);
        }
    }, [queryUser, user, setUser]);

    const isLoggedIn = Boolean(authorization?.token && user);

    const isAdmin = user?.roles?.includes(UserRole.Admin) ?? false;

    const canEditEvent = (event: EventDetailedModel) => {
        if (!user || !event) return false;

        // Admin can edit anything
        if (user.roles?.includes(UserRole.Admin)) return true;

        // Check if user is event owner
        if (event.owner?.userId === user.id) return true;

        // Check if user is event contributor
        if (event.contributors?.some((contributor) => contributor.user?.userId === user.id)) return true;

        return false;
    };

    const canEditSeries = (series: SeriesModel) => {
        if (!user || !series) return false;

        // Admin can edit anything
        if (user.roles?.includes(UserRole.Admin)) return true;

        // Check if user is series owner
        if (series.owner?.userId === user.id) return true;

        return false;
    };

    const login = async (credentials: Credentials) => {
        const result = await commands.login.mutate.mutateAsync({ credentials });
        const auth = result?.data;
        if (auth) {
            loginStore(credentials);
            authorizeStore(auth);
            setUserId(auth.id);
        } else {
            logoutStore();
        }
    };

    const forgotPassword = (email: string) => commands.forgotPassword.mutate.mutateAsync({ email });

    const changePassword = (newPassword: string) => commands.changePassword.mutate.mutateAsync({ newPassword });

    const resetPassword = (email: string, resetToken: string, newPassword: string) =>
        commands.resetPasswordWithToken.mutate.mutateAsync({ email, resetToken, newPassword });

    return {
        credentials,
        user,
        authorization,
        isLoggedIn,
        isAdmin,
        isHydrated: hydrated,
        canEditEvent,
        canEditSeries,
        login,
        logout: logoutStore,
        forgotPassword,
        resetPassword,
        changePassword,
        refreshUser: refetchUser,
    };
};
